import { ReactNode } from "react";
import { createRoot } from "react-dom/client";
import ezStore, { useStoreState } from "./ezStore";
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { StoreProvider } from "easy-peasy";
import Application from "components/Application";
import { filterLocalization, tableLocalization } from "components/utils/TableUtils";
import TableProvider from "components/table/TableProvider";
import { CoastguardProvider, UserDetails } from "@cofano/coastguard2-frontend-client";
import { DefaultTheme, ThemeProvider } from "@cofano/cck";
import { DatePickerLocale } from "@cofano/cck";
import { ApolloProvider } from "@apollo/client";
import client from "./apollo";
import LocaleWrapper from "./LocaleWrapper";
import { coastguardInstance } from "./security";
import { BOSUserDetails } from "types/commons";
import { DateUtils } from "utils/DateUtils";
import { GetUserDetailsDocument } from "__generated__/view/users/graphql/user.queries.generated";

import "./config/ZodConfig";
import "regenerator-runtime";
import "./styling/main.scss";

coastguardInstance.onSessionStarted(async (_instance, _event, _username, userDetails) => {
	await setSessionUser(userDetails);
});

coastguardInstance.onSessionUpdated(async (_instance, _event, userDetails) => {
	await setSessionUser(userDetails);
});

coastguardInstance.onSessionSwitched(async (_instance, _event, _username, userDetails) => {
	await client.clearStore();
	await setSessionUser(userDetails);
});

async function setSessionUser(userDetails: UserDetails) {

	const { data } = await client.query({
		query: GetUserDetailsDocument,
		variables: {
			userUuid: userDetails.accountUuid,
		},
	});

	const user: BOSUserDetails = {
		...userDetails,
		properties: {
			//@ts-expect-error
			...userDetails.properties,
			...data.getAccountByUuid,
		},
	};

	const { setUser } = ezStore.getActions().session;
	setUser({ user });
}

const root = createRoot(document.getElementById("root") as HTMLElement);

// eslint-disable-next-line react-refresh/only-export-components
const Provider = ({ children }: { children: ReactNode }) => {
	const { user } = useStoreState(store => store.session);
	const language = DateUtils.getDatePickerLocale(user?.language);
	return (
		<ThemeProvider
			theme={DefaultTheme}
			datePickerLocale={language as DatePickerLocale}
			dateFormat={DateUtils.getDateFormat(language)}
			timeFormat={DateUtils.getTimeFormat()}
			dateTimeFormat={DateUtils.getDateTimeFormat(language)}
			tableLocalization={tableLocalization}
			filterLocalization={filterLocalization}
			returnNullOnEmptyMultiSelect={true}
			showDarkModeToggleButton
		>
			{children}
		</ThemeProvider>
	);
};

root.render(
	<CoastguardProvider instance={coastguardInstance}>
		<StoreProvider store={ezStore}>
			<Provider>
				<ApolloProvider client={client}>
					<LocaleWrapper>
						<TableProvider>
							<Application />
						</TableProvider>
					</LocaleWrapper>
				</ApolloProvider>
			</Provider>
		</StoreProvider>
	</CoastguardProvider>,
);

